import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme
} from '@mui/material';
import {
  Inventory,
  Warning,
  TrendingDown,
  AttachMoney
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import DashboardService from '../../services/dashboard';
import MetricCard from '../../components/MetricCard';
import DateInterval from '../../components/calendar/DateInterval';

const InventoryDashboard = ({ storeId }) => {
  const theme = useTheme();
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.light,
    theme.palette.error.light
  ];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    date_from: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await DashboardService.getInventoryDashboard(storeId, dateRange);
      setData(result);
    } catch (err) {
      setError('Error al cargar el dashboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleDateChange = (start, end) => {
    if (start && end) {
      setDateRange({
        date_from: start.format('YYYY-MM-DD'),
        date_to: end.format('YYYY-MM-DD')
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No hay datos disponibles</Alert>;
  }

  return (
    <Box>
      {/* Date Filter */}
      <Box sx={{ mb: 3 }}>
        <DateInterval
          startDate={dateRange.date_from}
          endDate={dateRange.date_to}
          onChange={handleDateChange}
        />
      </Box>

      {/* Main Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Valor Total de Inventario"
            value={data.total_inventory_value}
            icon={AttachMoney}
            color="primary"
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total de Productos"
            value={data.total_products}
            icon={Inventory}
            color="success"
            format="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Stock Bajo"
            value={data.low_stock_products}
            icon={Warning}
            color="warning"
            format="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Sin Stock"
            value={data.out_of_stock_products}
            icon={TrendingDown}
            color="error"
            format="number"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Category Distribution Pie Chart */}
        {data.by_category && data.by_category.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Distribución por Categoría
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.by_category.map(cat => ({
                      name: cat.category_name,
                      value: cat.total_value
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => {
                      const percentage = data.total_inventory_value > 0 ? (entry.value / data.total_inventory_value * 100) : 0;
                      return `${entry.name}: ${percentage.toFixed(1)}%`;
                    }}
                    outerRadius={80}
                    fill={theme.palette.secondary.main}
                    dataKey="value"
                  >
                    {data.by_category.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value)} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Top Products by Value */}
        {data.top_value_products && data.top_value_products.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Top Productos por Valor
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.top_value_products.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="sku" />
                  <YAxis />
                  <Tooltip formatter={(value) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value)} />
                  <Bar dataKey="stock_value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Category Stats Table */}
      {data.by_category && data.by_category.length > 0 && (
        <Paper sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Inventario por Categoría
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Categoría</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Productos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stock Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Precio Promedio</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>% del Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_category.map((category) => (
                  <TableRow key={category.category_id} hover>
                    <TableCell>{category.category_name}</TableCell>
                    <TableCell align="right">{category.total_products}</TableCell>
                    <TableCell align="right">{category.total_stock}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(category.total_value)}
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(category.average_price)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(category.percentage || 0).toFixed(1)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Top Selling Products */}
      {data.top_selling_products && data.top_selling_products.length > 0 && (
        <Paper sx={{ mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Productos Más Vendidos
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ventas</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ingresos</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Margen</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.top_selling_products.map((product) => (
                  <TableRow key={product.article_id} hover>
                    <TableCell>{product.sku}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.category_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{product.total_sales}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.total_revenue)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${(product.profit_margin || 0).toFixed(1)}%`}
                        size="small"
                        color={(product.profit_margin || 0) > 30 ? 'success' : (product.profit_margin || 0) > 15 ? 'warning' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">{product.current_stock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Slow Moving Products */}
      {data.slow_moving_products && data.slow_moving_products.length > 0 && (
        <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Productos de Rotación Lenta
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Stock</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Valor Stock</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Última Venta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.slow_moving_products.map((product) => (
                  <TableRow key={product.article_id} hover>
                    <TableCell>{product.sku}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.category_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{product.current_stock}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(product.stock_value)}
                    </TableCell>
                    <TableCell align="right">
                      {product.last_sale_date ? new Date(product.last_sale_date).toLocaleDateString('es-AR') : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default InventoryDashboard;
